import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { residentConfirmPresentation } from '@/features/resident/constants/residentDialogProps'
import { ResidentDialogShell } from '@/features/resident/components/dialogs/ResidentDialogShell'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { reviewClaimSchema } from '../utils/reimbursementsValidators'
import type { ReviewClaimValues } from '../types/schemas'
import { reviewClaimStatus, updateClaimReviewLog } from '../services/reimbursementsService'
import type { ReviewClaimModalProps } from '../types'



const defaultValues: ReviewClaimValues = {
  claim_type: '',
  notes: '',
  reasons: {
    lease_end: false,
    partial_pay_delay_eviction: false,
    days_pass: false,
    incomplete_document: false,
  },
  verification_reasons: {
    moveindate: false,
    updated_ledger: false,
    maximum_claim: false,
    lease_expire: false,
    ledger_discrepancy: false,
  },
  limiting_factors: {
    multiple_payment: false,
    partial_pay_delay_eviction: false,
    delay_eviction: false,
    exceed_vacancy: false,
    missing_document: false,
    discrepancy: false,
    impact_covid: false,
    base_on_covid: false,
  },
}

const claimTypes = [
  {
    value: 'claim_received',
    title: 'Reimbursement Received - Under Review',
    description:
      'We have received your documentation. We may reach out to confirm various items as we review them.',
  },
  {
    value: 'claim_verification',
    title: 'Reimbursement Verification Needed',
    description:
      'We need more information as indicated below in order to process your reimbursement. Failure to provide the information noted will delay any potential settlements.',
  },
]

const limitingFactorOptions: Array<{ key: keyof ReviewClaimValues['limiting_factors']; label: string }> = [
  { key: 'multiple_payment', label: 'Multiple Partial Payments accepted' },
  { key: 'delay_eviction', label: 'Delayed Eviction or Filing' },
  { key: 'exceed_vacancy', label: 'Exceeded 6 Month Maximum Vacancy' },
  { key: 'missing_document', label: 'Required Documents were missing, delayed or incorrect' },
  { key: 'discrepancy', label: 'Discrepancy found in Ledger regarding Reimbursement impact on Rent' },
  { key: 'impact_covid', label: 'Reimbursement impacted by Covid-19 regulations and limitations' },
  { key: 'base_on_covid', label: 'Reimbursement based on Covid-19, but lacked adequate evidence or timely actions' },
]

const verificationReasonOptions: Array<
  { key: keyof ReviewClaimValues['verification_reasons']; label: string }
> = [
  { key: 'moveindate', label: 'New Resident Move-in Date for this unit' },
  { key: 'updated_ledger', label: 'Provide Final Ledger (include up to the new resident move-in)' },
  { key: 'maximum_claim', label: 'Maximum Reimbursement - Insurance Audit Ordered' },
  { key: 'lease_expire', label: 'Lease Expired prior to New Resident - Insurance Audit Ordered' },
  { key: 'ledger_discrepancy', label: 'Ledger Discrepancy (e.g. missing security deposit)' },
]

const reasonOptions: Array<{ key: keyof ReviewClaimValues['reasons']; label: string }> = [
  { key: 'lease_end', label: 'Lease Ended (beyond coverage period)' },
  {
    key: 'partial_pay_delay_eviction',
    label: 'Accepted Partial Payments that delayed Eviction and/or Filing with Court',
  },
  {
    key: 'days_pass',
    label:
      'More than 90 days have passed from Last Rent Payment before notifying Liberty Rent or providing appropriate, required legal documents',
  },
  { key: 'incomplete_document', label: 'Missing or Incomplete Required Documents' },
]

export function ReviewClaimModal({
  isOpen,
  onClose,
  onSuccess,
  defaultId,
  applicationId,
  reviewLogId,
  approvalType,
  approvalTypeValue,
  defaultType,
  adjustmentData,
  title = 'Review Default Reimbursement',
}: ReviewClaimModalProps) {
  const [sendToPropertyConfirmOpen, setSendToPropertyConfirmOpen] = useState(false)
  const [confirmChoice, setConfirmChoice] = useState<'yes' | 'no' | null>(null)
  const pendingSubmitValues = useRef<ReviewClaimValues | null>(null)
  const form = useForm<ReviewClaimValues>({
    resolver: zodResolver(reviewClaimSchema) as never,
    defaultValues,
  })
  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form watch() is intentionally unmemoizable
  const claimType = form.watch('claim_type')
  const limitingFactors = form.watch('limiting_factors')
  const allLimitingFactorsSelected = limitingFactorOptions.every((option) => limitingFactors?.[option.key])
  const someLimitingFactorsSelected = limitingFactorOptions.some((option) => limitingFactors?.[option.key])
  const verificationReasons = form.watch('verification_reasons')
  const allVerificationReasonsSelected = verificationReasonOptions.every(
    (option) => verificationReasons?.[option.key],
  )
  const someVerificationReasonsSelected = verificationReasonOptions.some(
    (option) => verificationReasons?.[option.key],
  )
  const reasons = form.watch('reasons')
  const allReasonsSelected = reasonOptions.every((option) => reasons?.[option.key])
  const someReasonsSelected = reasonOptions.some((option) => reasons?.[option.key])

  const limitingMasterRef = useRef<HTMLInputElement | null>(null)
  const verificationMasterRef = useRef<HTMLInputElement | null>(null)
  const reasonsMasterRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (isOpen) {
      form.reset(defaultValues)
      if (defaultType) {
        form.setValue('claim_type', defaultType, { shouldDirty: false })
      }
      pendingSubmitValues.current = null
      setSendToPropertyConfirmOpen(false)
    }
  }, [defaultType, form, isOpen])

  useEffect(() => {
    if (!limitingMasterRef.current) return
    limitingMasterRef.current.indeterminate = !allLimitingFactorsSelected && someLimitingFactorsSelected
  }, [allLimitingFactorsSelected, someLimitingFactorsSelected])

  useEffect(() => {
    if (!verificationMasterRef.current) return
    verificationMasterRef.current.indeterminate =
      !allVerificationReasonsSelected && someVerificationReasonsSelected
  }, [allVerificationReasonsSelected, someVerificationReasonsSelected])

  useEffect(() => {
    if (!reasonsMasterRef.current) return
    reasonsMasterRef.current.indeterminate = !allReasonsSelected && someReasonsSelected
  }, [allReasonsSelected, someReasonsSelected])

  useEffect(() => {
    if (!claimType) return
    if (claimType === 'claim_received' || claimType === 'RECEIVED') {
      verificationReasonOptions.forEach((option) => {
        form.setValue(`verification_reasons.${option.key}`, false, { shouldDirty: true })
      })
      reasonOptions.forEach((option) => {
        form.setValue(`reasons.${option.key}`, false, { shouldDirty: true })
      })
      return
    }
    if (claimType === 'claim_verification' || claimType === 'VERIFICATION_NEEDED') {
      limitingFactorOptions.forEach((option) => {
        form.setValue(`limiting_factors.${option.key}`, false, { shouldDirty: true })
      })
      reasonOptions.forEach((option) => {
        form.setValue(`reasons.${option.key}`, false, { shouldDirty: true })
      })
      return
    }
    if (
      claimType === 'claim_reopened' ||
      claimType === 'REOPENED' ||
      claimType === 'OPEN'
    ) {
      limitingFactorOptions.forEach((option) => {
        form.setValue(`limiting_factors.${option.key}`, false, { shouldDirty: true })
      })
      verificationReasonOptions.forEach((option) => {
        form.setValue(`verification_reasons.${option.key}`, false, { shouldDirty: true })
      })
      reasonOptions.forEach((option) => {
        form.setValue(`reasons.${option.key}`, false, { shouldDirty: true })
      })
    }
  }, [claimType, form])

  const submitReview = async (values: ReviewClaimValues, emailSend: boolean) => {
    try {
      const withEmailOption = {
        ...values,
        email_send: emailSend,
        ...(adjustmentData ? { adjustment_data: adjustmentData } : {}),
        ...(approvalType ? { approvaltype: approvalType } : {}),
      }
      if (reviewLogId && approvalType) {
        await updateClaimReviewLog(reviewLogId, {
          page: 'default',
          application_id: applicationId,
          [approvalType]: approvalTypeValue ?? '1',
          review_decision: 'Approved',
          review_remark: values.notes,
        })
      }
      await reviewClaimStatus(defaultId, withEmailOption)
      showSuccess('Default Reimbursement Updated Successfully!')
      onSuccess()
      onClose()
    } catch (error) {
      showApiError(error)
    }
  }

  const onSubmit = (values: ReviewClaimValues) => {
    pendingSubmitValues.current = values
    setSendToPropertyConfirmOpen(true)
  }

  const completeSendToPropertyChoice = async (emailSend: boolean) => {
    const values = pendingSubmitValues.current
    if (!values) {
      setSendToPropertyConfirmOpen(false)
      return
    }
    if (confirmChoice) return
    setConfirmChoice(emailSend ? 'yes' : 'no')
    try {
      await submitReview(values, emailSend)
      setSendToPropertyConfirmOpen(false)
      pendingSubmitValues.current = null
    } finally {
      setConfirmChoice(null)
    }
  }

  return (
    <>
    <ResidentDialogShell
      isOpen={isOpen}
      title={title}
      headerVariant="centeredTitle"
      onClose={onClose}
      maxWidth="max-w-3xl"
      bodyScrollOnly
      panelDataTestId="review-claim-modal-panel"
      footer={
        <div
          className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"
          data-testid="review-claim-modal-footer"
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="review-claim-modal-cancel-button"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={form.formState.isSubmitting}
            onClick={() => void form.handleSubmit(onSubmit)()}
            data-testid="review-claim-modal-submit-button"
            className="w-full rounded-lg bg-button px-3 py-2 text-sm font-semibold text-white sm:w-auto disabled:opacity-60"
          >
            {form.formState.isSubmitting ? 'Please wait…' : 'Submit'}
          </button>
        </div>
      }
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 pr-1 text-sm">
        <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 leading-6 text-slate-700">
          Note: These actions will send the checked response to the property indicating that we have reviewed their
          documents and are either going to proceed with calculating a settlement, or we are not.
        </p>

        <div className="space-y-3">
          {!defaultType ? (
            claimTypes.map((type) => (
              <div
                key={type.value}
                className="rounded-md border border-slate-200 bg-slate-50/50 px-4 py-3"
              >
                <label className="flex cursor-pointer items-start gap-3 rounded-sm py-0.5">
                  <input
                    type="radio"
                    value={type.value}
                    {...form.register('claim_type')}
                    className="mt-1 h-4 w-4"
                  />
                  <span className="text-sm leading-6 text-slate-700">
                    <span className="font-semibold text-slate-800">{type.title}</span>
                    {' : '}
                    {type.description}
                  </span>
                </label>

                {claimType === type.value && type.value === 'claim_received' ? (
                  <div className="mt-4 space-y-4 border-l border-slate-200 pl-7">
                    <fieldset className="rounded-md border border-slate-200 bg-white p-4">
                      <legend className="px-1 text-sm font-semibold text-slate-700">
                        <label className="inline-flex items-center gap-2">
                          <input
                            ref={limitingMasterRef}
                            type="checkbox"
                            checked={allLimitingFactorsSelected}
                            onChange={(event) => {
                              const nextChecked = event.target.checked
                              limitingFactorOptions.forEach((option) => {
                                form.setValue(`limiting_factors.${option.key}`, nextChecked, {
                                  shouldDirty: true,
                                })
                              })
                            }}
                            className="h-4 w-4"
                          />
                          Limiting Factors :
                        </label>
                      </legend>

                      <div className="space-y-2">
                        {limitingFactorOptions.map((option) => (
                          <label key={option.key} className="flex items-start gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              {...form.register(`limiting_factors.${option.key}`)}
                              className="mt-1 h-4 w-4"
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <label className="block space-y-2">
                      <span className="text-sm font-semibold text-slate-700">Additional Clarifications :</span>
                      <textarea
                        {...form.register('notes')}
                        rows={4}
                        className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700"
                      />
                    </label>
                  </div>
                ) : null}

                {claimType === type.value && type.value === 'claim_verification' ? (
                  <div className="mt-4 space-y-4 border-l border-slate-200 pl-7">
                    <fieldset className="rounded-md border border-slate-200 bg-white p-4">
                      <legend className="px-1 text-sm font-semibold text-slate-700">
                        <label className="inline-flex items-center gap-2">
                          <input
                            ref={verificationMasterRef}
                            type="checkbox"
                            checked={allVerificationReasonsSelected}
                            onChange={(event) => {
                              const nextChecked = event.target.checked
                              verificationReasonOptions.forEach((option) => {
                                form.setValue(`verification_reasons.${option.key}`, nextChecked, {
                                  shouldDirty: true,
                                })
                              })
                            }}
                            className="h-4 w-4"
                          />
                          Reasons:
                        </label>
                      </legend>
                      <div className="space-y-2">
                        {verificationReasonOptions.map((option) => (
                          <label key={option.key} className="flex items-start gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              {...form.register(`verification_reasons.${option.key}`)}
                              className="mt-1 h-4 w-4"
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <label className="block space-y-2">
                      <span className="text-sm font-semibold text-slate-700">Additional Clarifications :</span>
                      <textarea
                        {...form.register('notes')}
                        rows={4}
                        className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700"
                      />
                    </label>
                  </div>
                ) : null}
              </div>
            ))
          ) : null}

          {defaultType === 'claim_denied' ? (
            <div className="rounded-md border border-slate-200 bg-slate-50/50 px-4 py-3">
              <label className="flex cursor-pointer items-start gap-3 rounded-sm py-0.5">
                <input
                  type="radio"
                  value="claim_denied"
                  {...form.register('claim_type')}
                  className="mt-1 h-4 w-4"
                />
                <span className="text-sm leading-6 text-slate-700">
                  <span className="font-semibold text-slate-800">Denied</span>
                  {' : '}
                  We have reviewed your reimbursement and have found that you have not provided the correct types of required documentation or in a timely manner as described in our certificate.
                  <br />
                  The following &apos;Reasons&apos; or &apos;Limiting Factors&apos; support why your reimbursement was denied.
                </span>
              </label>

              {claimType === 'claim_denied' ? (
                <div className="mt-4 space-y-4 border-l border-slate-200 pl-7">
                  <fieldset className="rounded-md border border-slate-200 bg-white p-4">
                    <legend className="px-1 text-sm font-semibold text-slate-700">
                      <label className="inline-flex items-center gap-2">
                        <input
                          ref={reasonsMasterRef}
                          type="checkbox"
                          checked={allReasonsSelected}
                          onChange={(event) => {
                            const nextChecked = event.target.checked
                            reasonOptions.forEach((option) => {
                              form.setValue(`reasons.${option.key}`, nextChecked, {
                                shouldDirty: true,
                              })
                            })
                          }}
                          className="h-4 w-4"
                        />
                        Reasons:
                      </label>
                    </legend>
                    <div className="space-y-2">
                      {reasonOptions.map((option) => (
                        <label key={option.key} className="flex items-start gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            {...form.register(`reasons.${option.key}`)}
                            className="mt-1 h-4 w-4"
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <fieldset className="rounded-md border border-slate-200 bg-white p-4">
                    <legend className="px-1 text-sm font-semibold text-slate-700">
                      <label className="inline-flex items-center gap-2">
                        <input
                          ref={limitingMasterRef}
                          type="checkbox"
                          checked={allLimitingFactorsSelected}
                          onChange={(event) => {
                            const nextChecked = event.target.checked
                            limitingFactorOptions.forEach((option) => {
                              form.setValue(`limiting_factors.${option.key}`, nextChecked, {
                                shouldDirty: true,
                              })
                            })
                          }}
                          className="h-4 w-4"
                        />
                        Limiting Factors :
                      </label>
                    </legend>
                    <div className="space-y-2">
                      {limitingFactorOptions.map((option) => (
                        <label key={option.key} className="flex items-start gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            {...form.register(`limiting_factors.${option.key}`)}
                            className="mt-1 h-4 w-4"
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <label className="block space-y-2">
                    <span className="text-sm font-semibold text-slate-700">
                      Additional Clarifications :
                      <span className="block text-xs font-normal text-slate-500">
                        The following will provide specific information regarding your reimbursement that prevented it from being accepted.
                      </span>
                    </span>
                    <textarea
                      {...form.register('notes')}
                      rows={4}
                      className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700"
                    />
                  </label>
                </div>
              ) : null}
            </div>
          ) : null}

          {defaultType === 'claim_reopened' ? (
            <div className="rounded-md border border-slate-200 bg-slate-50/50 px-4 py-3">
              <label className="flex cursor-pointer items-start gap-3 rounded-sm py-0.5">
                <input
                  type="radio"
                  value="claim_reopened"
                  {...form.register('claim_type')}
                  className="mt-1 h-4 w-4"
                />
                <span className="text-sm leading-6 text-slate-700">
                  <span className="font-semibold text-slate-800">Reimbursement Reopened</span>
                  {' : '}
                  We have re-opened you reimbursement for further review per direction from Liberty Management&apos;s leadership team. We will provide additional feedback shortly regarding your claim.
                </span>
              </label>

              {claimType === 'claim_reopened' ? (
                <div className="mt-4 space-y-4 border-l border-slate-200 pl-7">
                  <label className="block space-y-2">
                    <span className="text-sm font-semibold text-slate-700">Additional Clarifications :</span>
                    <textarea
                      {...form.register('notes')}
                      rows={4}
                      className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700"
                    />
                  </label>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        {form.formState.errors.claim_type && (
          <span className="block text-xs text-red-600">{form.formState.errors.claim_type.message}</span>
        )}
      </form>
    </ResidentDialogShell>
    <ConfirmDialog
      isOpen={sendToPropertyConfirmOpen}
      title="Send to Property?"
      message="Do you want to send a notice to the Property?"
      primaryLabel="Yes"
      secondaryLabel="No"
      primaryLoading={confirmChoice === 'yes'}
      secondaryLoading={confirmChoice === 'no'}
      disabled={confirmChoice !== null}
      onPrimary={() => void completeSendToPropertyChoice(true)}
      onSecondary={() => void completeSendToPropertyChoice(false)}
      onDismiss={() => {
        if (confirmChoice === null) {
          setSendToPropertyConfirmOpen(false)
          pendingSubmitValues.current = null
        }
      }}
      {...residentConfirmPresentation}
    />
    </>
  )
}

